import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  MenuItem,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  LocalShipping,
  AttachMoney,
  Scale,
  Schedule,
  EmojiObjects
} from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
dayjs.extend(isBetween);

// ─── Styled ───────────────────────────────────────────────────────────────────

const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  height: '100%'
}));

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  borderTop: '3px solid',
  height: '100%'
}));

const TH = styled(TableCell)(() => ({
  fontWeight: 700,
  fontSize: '0.72rem',
  color: '#555',
  padding: '6px 10px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e0e0e0',
  whiteSpace: 'nowrap'
}));

const TD = styled(TableCell)(() => ({
  fontSize: '0.72rem',
  padding: '5px 10px',
  borderBottom: '1px solid #f3f3f3'
}));

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];
const MODE_COLORS = ['#1565C0', '#00897B', '#FFA000', '#8E24AA', '#546E7A'];
const YEAR_COLORS = [
  '#1565C0',
  '#2e7d32',
  '#e65100',
  '#6a1b9a',
  '#00695c',
  '#c62828'
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtM = (val, isCurr = false) => {
  if (!val || isNaN(val)) return isCurr ? '$0' : '0';
  if (val >= 1_000_000)
    return (isCurr ? '$' : '') + (val / 1_000_000).toFixed(2) + 'M';
  if (val >= 1_000) return (isCurr ? '$' : '') + (val / 1_000).toFixed(1) + 'K';
  return (isCurr ? '$' : '') + Math.round(val).toLocaleString();
};

const pctDiff = (curr, prev) => {
  if (!prev || prev === 0) return null;
  return (((curr - prev) / Math.abs(prev)) * 100).toFixed(1);
};

const buildRangeLabel = (start, end) => {
  return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
};

/**
 * Short column header for tables/charts — shows year range like "2024–26"
 * or just "2025" if single year.
 */
const buildShortLabel = (start, end) => {
  if (start.year() === end.year()) return start.format('YYYY');
  return `${start.format('YYYY')}–${end.format('YY')}`;
};

// ─── Delta Badge ──────────────────────────────────────────────────────────────

const Delta = ({ curr, prev, lowerIsBetter = false, isPts = false }) => {
  const p = pctDiff(curr, prev);
  if (p === null)
    return (
      <Typography variant='caption' color='text.disabled'>
        —
      </Typography>
    );
  const val = parseFloat(p);
  const isUp = val > 0;
  const isGood = lowerIsBetter ? !isUp : isUp;
  const color = val === 0 ? '#888' : isGood ? '#2e7d32' : '#d32f2f';

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
      {isUp ? (
        <TrendingUp sx={{ fontSize: 13, color }} />
      ) : (
        <TrendingDown sx={{ fontSize: 13, color }} />
      )}
      <Typography
        variant='caption'
        sx={{ color, fontWeight: 700, fontSize: '0.7rem' }}
      >
        {isUp ? '+' : ''}
        {isPts ? (curr - prev).toFixed(1) + ' pts' : p + '%'}
      </Typography>
    </Box>
  );
};

// ─── Compute all metrics from a shipments array ───────────────────────────────

const computeMetrics = ships => {
  const freight = ships.reduce((s, i) => s + (i.quoteNetCharge || 0), 0);
  const count = ships.length;
  const weight = ships.reduce((s, i) => s + (i.totalNetWeight || 0), 0);
  const costPerLb = weight > 0 ? freight / weight : 0;

  // OTD — LTL Outbound Delivered only
  const ltlDel = ships.filter(
    s =>
      s.shipmentStatus === 'Delivered' &&
      s.shipmentType === 'LTL' &&
      s.shipmentTypeRule === 'Outbound' &&
      s.actualDeliveryDate &&
      s.scheduledDeliveryDate
  );
  const onTime = ltlDel.filter(
    s => new Date(s.actualDeliveryDate) <= new Date(s.scheduledDeliveryDate)
  );
  const otdPct = ltlDel.length > 0 ? (onTime.length / ltlDel.length) * 100 : 0;

  // Monthly freight — keyed by calendar month name only (Jan, Feb…)
  // so that curr & prev months align on the same axis regardless of year
  const monthMap = {};
  ships.forEach(s => {
    if (!s.actualPickupDate) return;
    const d = dayjs(s.actualPickupDate);
    const key = MONTH_NAMES[d.month()]; // just "Jan", "Feb"…
    if (!monthMap[key]) monthMap[key] = { freight: 0, order: d.month() };
    monthMap[key].freight += s.quoteNetCharge || 0;
  });
  const sortedMonths = Object.entries(monthMap).sort(
    (a, b) => a[1].order - b[1].order
  );
  let cum = 0;
  const monthlyData = sortedMonths.map(([key, v]) => {
    cum += v.freight;
    return { month: key, freight: v.freight, cumFreight: cum };
  });

  // Freight by shipment type
  const modeMap = {};
  ships.forEach(s => {
    const mode = s.shipmentType || 'Other';
    modeMap[mode] = (modeMap[mode] || 0) + (s.quoteNetCharge || 0);
  });

  // Top 5 lanes (state→state)
  const laneMap = {};
  ships.forEach(s => {
    if (!s.originState || !s.destinationState) return;
    const key = `${s.originState} → ${s.destinationState}`;
    laneMap[key] = (laneMap[key] || 0) + (s.quoteNetCharge || 0);
  });
  const top5Lanes = Object.entries(laneMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([lane, spend]) => ({ lane, spend }));

  // Top 5 warehouse locations (City, State format)
  const facMap = {};
  ships.forEach(s => {
    const city = s.originCity?.trim();
    const state = s.originState?.trim();
    const fac =
      city && state ? `${city}, ${state}` : city || state || 'Unknown';
    facMap[fac] = (facMap[fac] || 0) + (s.quoteNetCharge || 0);
  });
  const top5Fac = Object.entries(facMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, spend]) => ({ name, spend }));

  return {
    freight,
    count,
    weight,
    costPerLb,
    otdPct,
    monthlyData,
    modeMap,
    top5Lanes,
    top5Fac
  };
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AVAILABLE_YEARS = [2024, 2025, 2026];

const YOYFreightTab = ({
  allShipments = []
}) => {
  const today = dayjs();
  const currentYear = today.year();
  const lastYear = currentYear - 1;

  // Only months that have started this year (no future months)
  const ytdMonths = MONTH_NAMES.slice(0, today.month() + 1);

  const [selectedMonths, setSelectedMonths] = useState(ytdMonths);
  const [selectedYears, setSelectedYears] = useState([lastYear, currentYear]);

  const monthIndices = useMemo(
    () => selectedMonths.map(m => MONTH_NAMES.indexOf(m)),
    [selectedMonths]
  );

  const sortedYears = useMemo(
    () => [...selectedYears].sort((a, b) => a - b),
    [selectedYears]
  );

  const perYearData = useMemo(
    () =>
      sortedYears.map((year, idx) => {
        const shipments = allShipments.filter(s => {
          if (!s.actualPickupDate) return false;
          const d = dayjs(s.actualPickupDate);
          if (d.year() !== year) return false;
          if (!monthIndices.includes(d.month())) return false;
          // YTD cap: never show dates beyond today's month/day
          if (d.month() > today.month()) return false;
          if (d.month() === today.month() && d.date() > today.date()) return false;
          return true;
        });
        return {
          year,
          color: YEAR_COLORS[idx % YEAR_COLORS.length],
          metrics: computeMetrics(shipments)
        };
      }),
    [allShipments, sortedYears, monthIndices] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Latest year = KPI "current", previous year = KPI "prev" for trend arrows
  const latestData = perYearData[perYearData.length - 1];
  const prevData =
    perYearData.length >= 2 ? perYearData[perYearData.length - 2] : null;
  const curr = latestData?.metrics || computeMetrics([]);
  const prevMetrics = prevData?.metrics || null;

  // ── Monthly grouped bar chart ─────────────────────────────────────────────
  const monthlyChartData = useMemo(() => {
    const allMons = [
      ...new Set(
        perYearData.flatMap(d => d.metrics.monthlyData.map(m => m.month))
      )
    ].sort((a, b) => MONTH_NAMES.indexOf(a) - MONTH_NAMES.indexOf(b));
    const series = perYearData.map(d => ({
      name: String(d.year),
      color: d.color,
      data: allMons.map(m =>
        Math.round(
          d.metrics.monthlyData.find(md => md.month === m)?.freight || 0
        )
      )
    }));
    return { categories: allMons, series };
  }, [perYearData]);

  // ── Cumulative trend lines ────────────────────────────────────────────────
  const cumulativeData = useMemo(() => {
    const allMons = [
      ...new Set(
        perYearData.flatMap(d => d.metrics.monthlyData.map(m => m.month))
      )
    ].sort((a, b) => MONTH_NAMES.indexOf(a) - MONTH_NAMES.indexOf(b));
    const series = perYearData.map(d => ({
      name: String(d.year),
      color: d.color,
      data: allMons.map(m =>
        Math.round(
          d.metrics.monthlyData.find(md => md.month === m)?.cumFreight || 0
        )
      )
    }));
    return { categories: allMons, series };
  }, [perYearData]);

  // ── Mode table (one column per year) ─────────────────────────────────────
  const modeTableData = useMemo(() => {
    const allModes = [
      ...new Set(perYearData.flatMap(d => Object.keys(d.metrics.modeMap)))
    ];
    return allModes
      .map(mode => ({
        mode,
        values: perYearData.map(d => d.metrics.modeMap[mode] || 0)
      }))
      .sort(
        (a, b) => b.values[b.values.length - 1] - a.values[a.values.length - 1]
      );
  }, [perYearData]);

  const modeDonutSeries = Object.values(curr.modeMap || {});
  const modeDonutLabels = Object.keys(curr.modeMap || {});

  // ── Facility chart (one series per year) ─────────────────────────────────
  const facilityData = useMemo(() => {
    const allFacs = [
      ...new Set(perYearData.flatMap(d => d.metrics.top5Fac.map(f => f.name)))
    ].slice(0, 5);
    const series = perYearData.map(d => ({
      name: String(d.year),
      color: d.color,
      data: allFacs.map(
        n => d.metrics.top5Fac.find(f => f.name === n)?.spend || 0
      )
    }));
    return { names: allFacs, series };
  }, [perYearData]);

  // ── Insights ──────────────────────────────────────────────────────────────
  const insights = useMemo(() => {
    const list = [];
    const topMode = modeTableData[0];
    if (curr.freight)
      list.push({
        color: '#1565C0',
        text: `Total freight spend: ${fmtM(curr.freight, true)} across ${curr.count} shipments (${latestData?.year}).`
      });
    if (topMode)
      list.push({
        color: '#2e7d32',
        text: `Top shipping mode: ${topMode.mode} at ${fmtM(topMode.values[topMode.values.length - 1], true)}.`
      });
    if (curr.costPerLb)
      list.push({
        color: '#e65100',
        text: `Average cost per lb: $${curr.costPerLb.toFixed(2)}.`
      });
    if (curr.otdPct)
      list.push({
        color: '#6a1b9a',
        text: `On-time delivery rate: ${curr.otdPct.toFixed(1)}%.`
      });
    return list;
  }, [curr, modeTableData, latestData]);

  const hasData = perYearData.length > 0;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ backgroundColor: '#f5f7fa', pb: 1 }}>
      {/* ── Header + Toggle ─────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
          px: 0.5
        }}
      >
        <Box>
          <Typography
            variant='h6'
            fontWeight='bold'
            fontSize='1rem'
            color='#1a237e'
          >
            Year-to-Date Freight Dashboard
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            fontSize='0.7rem'
          >
            {selectedMonths.length === ytdMonths.length
              ? 'All YTD months'
              : selectedMonths.join(', ')}{' '}
            · {selectedYears.join(', ')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Month multi-select — only YTD months (no future months) */}
          <FormControl size='small' sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: 12 }}>Months</InputLabel>
            <Select
              multiple
              value={selectedMonths}
              onChange={e => {
                const val = e.target.value;
                if (val[val.length - 1] === '__all__') {
                  setSelectedMonths(
                    selectedMonths.length === ytdMonths.length ? [] : ytdMonths
                  );
                  return;
                }
                setSelectedMonths(val);
              }}
              input={<OutlinedInput label='Months' />}
              renderValue={sel =>
                sel.length === ytdMonths.length ? 'All YTD Months' : sel.join(', ')
              }
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
              sx={{ fontSize: 12 }}
            >
              <MenuItem value='__all__' dense>
                <Checkbox
                  checked={selectedMonths.length === ytdMonths.length}
                  indeterminate={
                    selectedMonths.length > 0 &&
                    selectedMonths.length < ytdMonths.length
                  }
                  size='small'
                />
                <ListItemText
                  primary='Select All'
                  primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                />
              </MenuItem>
              {ytdMonths.map(m => (
                <MenuItem key={m} value={m} dense>
                  <Checkbox checked={selectedMonths.includes(m)} size='small' />
                  <ListItemText
                    primary={m}
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Year multi-select — fixed to 2024, 2025, 2026 */}
          <FormControl size='small' sx={{ minWidth: 130 }}>
            <InputLabel sx={{ fontSize: 12 }}>Years</InputLabel>
            <Select
              multiple
              value={selectedYears}
              onChange={e =>
                setSelectedYears(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',').map(Number)
                    : e.target.value
                )
              }
              input={<OutlinedInput label='Years' />}
              renderValue={sel => sel.join(', ')}
              MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
              sx={{ fontSize: 12 }}
            >
              {AVAILABLE_YEARS.map(y => (
                <MenuItem key={y} value={y} dense>
                  <Checkbox checked={selectedYears.includes(y)} size='small' />
                  <ListItemText
                    primary={y}
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* ── KPI Stat Cards ──────────────────────────────────────────────── */}
      <Grid container spacing={0.75} sx={{ mb: 1 }}>
        {[
          {
            title: 'Total Freight Spend',
            icon: <AttachMoney sx={{ fontSize: 22, color: '#1565C0' }} />,
            currVal: fmtM(curr.freight, true),
            exactVal: `$${(curr.freight || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            currRaw: curr.freight,
            prevRaw: prevMetrics?.freight,
            lowerIsBetter: true,
            color: '#1565C0'
          },
          {
            title: 'Total Shipments',
            icon: <LocalShipping sx={{ fontSize: 22, color: '#2e7d32' }} />,
            currVal: curr.count.toLocaleString(),
            exactVal: curr.count.toLocaleString(),
            currRaw: curr.count,
            prevRaw: prevMetrics?.count,
            lowerIsBetter: false,
            color: '#2e7d32'
          },
          {
            title: 'Total Weight (lbs)',
            icon: <Scale sx={{ fontSize: 22, color: '#e65100' }} />,
            currVal: fmtM(curr.weight),
            exactVal: `${(curr.weight || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs`,
            currRaw: curr.weight,
            prevRaw: prevMetrics?.weight,
            lowerIsBetter: false,
            color: '#e65100'
          },
          {
            title: 'Cost per lbs',
            icon: <AttachMoney sx={{ fontSize: 22, color: '#6a1b9a' }} />,
            currVal: `$${curr.costPerLb.toFixed(2)}`,
            exactVal: `$${curr.costPerLb?.toFixed(4)} / lb`,
            currRaw: curr.costPerLb,
            prevRaw: prevMetrics?.costPerLb,
            lowerIsBetter: true,
            color: '#6a1b9a'
          }
        ].map((card, i) => (
          <Grid item xs={12} sm={6} md key={i} sx={{ flex: '1 1 0' }}>
            <StatCard elevation={1} sx={{ borderTopColor: card.color }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}
              >
                <Typography
                  variant='caption'
                  color='text.secondary'
                  fontWeight={600}
                  fontSize='0.7rem'
                >
                  {card.title}
                </Typography>
                {card.icon}
              </Box>
              <Tooltip
                title={
                  <Typography sx={{ fontSize: 13 }}>{card.exactVal}</Typography>
                }
                placement='top'
                arrow
              >
                <Typography
                  variant='h5'
                  fontWeight='bold'
                  sx={{
                    fontSize: '1.4rem',
                    color: '#1a1a2e',
                    lineHeight: 1.2,
                    cursor: 'pointer'
                  }}
                >
                  {card.currVal}
                </Typography>
              </Tooltip>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Delta
                  curr={card.currRaw}
                  prev={card.prevRaw}
                  lowerIsBetter={card.lowerIsBetter}
                />
                {prevData && (
                  <Typography
                    variant='caption'
                    color='text.disabled'
                    fontSize='0.65rem'
                  >
                    vs {prevData.year}
                  </Typography>
                )}
              </Box>
            </StatCard>
          </Grid>
        ))}
      </Grid>

      {/* ── Row 2: Monthly bar + Mode donut+table ───────────────────────── */}
      <Grid container spacing={0.75} sx={{ mb: 1 }}>
        {/* Freight Spend grouped bar */}
        <Grid item xs={12} md={6}>
          <SectionPaper elevation={1}>
            <Typography
              variant='subtitle2'
              fontWeight='bold'
              fontSize='0.85rem'
              mb={0.5}
            >
              Freight Spend {'YTD'}
            </Typography>
            {/* Legend — labels come from actual date ranges */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}
            >
              {perYearData.map(d => (
                <Box
                  key={d.year}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: 1,
                      backgroundColor: d.color
                    }}
                  />
                  <Typography variant='caption' fontSize='0.68rem'>
                    {d.year}
                  </Typography>
                </Box>
              ))}
            </Box>
            {hasData && (
              <ReactApexChart
                key={`bar-ytd-${currentYear}`}
                options={{
                  chart: { type: 'bar', height: 200, toolbar: { show: false } },
                  plotOptions: {
                    bar: { columnWidth: '70%', grouped: true, borderRadius: 2 }
                  },
                  dataLabels: { enabled: false },
                  xaxis: {
                    categories: monthlyChartData.categories,
                    labels: { style: { fontSize: '10px' } }
                  },
                  yaxis: {
                    labels: {
                      formatter: val =>
                        val >= 1000
                          ? '$' + (val / 1000).toFixed(0) + 'K'
                          : '$0',
                      style: { fontSize: '10px' }
                    }
                  },
                  colors: perYearData.map(d => d.color),
                  legend: { show: false },
                  grid: { padding: { left: 5, right: 5, top: 0, bottom: 0 } },
                  tooltip: {
                    shared: true,
                    intersect: false,
                    y: { formatter: val => '$' + val.toLocaleString() }
                  }
                }}
                series={monthlyChartData.series}
                type='bar'
                height={200}
              />
            )}
          </SectionPaper>
        </Grid>

        {/* Freight Spend by Mode */}
        <Grid item xs={12} md={6}>
          <SectionPaper elevation={1}>
            <Typography
              variant='subtitle2'
              fontWeight='bold'
              fontSize='0.85rem'
              mb={0.5}
            >
              Freight Spend by Mode {'YTD'}
            </Typography>
            <Grid container spacing={1}>
              {/* Donut */}
              <Grid item xs={5}>
                {hasData && (
                  <ReactApexChart
                    key={`donut-ytd-${currentYear}`}
                    options={{
                      chart: { type: 'donut', height: 190 },
                      labels: modeDonutLabels,
                      colors: MODE_COLORS,
                      legend: { show: false },
                      dataLabels: { enabled: false },
                      plotOptions: {
                        pie: {
                          donut: {
                            size: '60%',
                            labels: {
                              show: true,
                              name: {
                                // ← add this
                                show: true,
                                fontSize: '10px',
                                fontWeight: 600
                              },
                              value: {
                                // ← THIS is what shows on hover — was missing
                                show: true,
                                fontSize: '13px',
                                fontWeight: 800,
                                formatter: val => fmtM(parseFloat(val), true) // ← formats to $1.2M
                              },
                              total: {
                                show: true,
                                label: `Total Spend`,
                                fontSize: '11px',
                                fontWeight: 700,
                                formatter: val => fmtM(curr.freight, true)
                              }
                            }
                          }
                        }
                      },
                      tooltip: {
                        y: { formatter: val => fmtM(val, true) }
                      }
                    }}
                    series={modeDonutSeries.length ? modeDonutSeries : [1]}
                    type='donut'
                    height={190}
                  />
                )}
              </Grid>
              {/* Mode comparison table — column headers from dynamic short labels */}
              <Grid item xs={7}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TH>Mode</TH>
                      {perYearData.map(d => (
                        <TH key={d.year} align='right' sx={{ color: d.color }}>
                          {d.year}
                        </TH>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modeTableData.map((row, i) => (
                      <TableRow
                        key={i}
                        sx={{ '&:hover': { backgroundColor: '#fafafa' } }}
                      >
                        <TD>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: MODE_COLORS[i] || '#ccc',
                                flexShrink: 0
                              }}
                            />
                            {row.mode}
                          </Box>
                        </TD>
                        {row.values.map((val, vi) => (
                          <TD key={vi} align='right' sx={{ fontWeight: 600 }}>
                            {fmtM(val, true)}
                          </TD>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          </SectionPaper>
        </Grid>
      </Grid>

      {/* ── Row 3: Top 5 Lanes + Facility + Cumulative Trend ────────────── */}
      <Grid container spacing={0.75} sx={{ mb: 1 }}>
        {/* Top 5 Lanes table */}
        <Grid item xs={12} md={3}>
          <SectionPaper elevation={1}>
            <Typography
              variant='subtitle2'
              fontWeight='bold'
              fontSize='0.85rem'
              mb={1}
            >
              Top 5 Lanes by Spend {'YTD'}
            </Typography>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TH>Lane</TH>
                  {perYearData.map(d => (
                    <TH key={d.year} align='right' sx={{ color: d.color }}>
                      {d.year}
                    </TH>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {curr.top5Lanes.length === 0 ? (
                  <TableRow>
                    <TD
                      colSpan={4}
                      align='center'
                      sx={{ color: '#bbb', py: 2 }}
                    >
                      No data
                    </TD>
                  </TableRow>
                ) : (
                  (() => {
                    const allLanes = [
                      ...new Set(
                        perYearData.flatMap(d =>
                          d.metrics.top5Lanes.map(l => l.lane)
                        )
                      )
                    ].slice(0, 5);
                    return allLanes.map((lane, i) => (
                      <TableRow
                        key={i}
                        sx={{ '&:hover': { backgroundColor: '#fafafa' } }}
                      >
                        <TD sx={{ fontWeight: 600, color: '#1a237e' }}>
                          {lane}
                        </TD>
                        {perYearData.map(d => {
                          const found = d.metrics.top5Lanes.find(
                            l => l.lane === lane
                          );
                          return (
                            <TD
                              key={d.year}
                              align='right'
                              sx={{ fontWeight: 600 }}
                            >
                              {fmtM(found?.spend || 0, true)}
                            </TD>
                          );
                        })}
                      </TableRow>
                    ));
                  })()
                )}
              </TableBody>
            </Table>
          </SectionPaper>
        </Grid>

        {/* Spend by Facility */}
        <Grid item xs={12} md={5}>
          <SectionPaper elevation={1}>
            <Typography
              variant='subtitle2'
              fontWeight='bold'
              fontSize='0.85rem'
              mb={0.5}
            >
              Spend by Ship From Facility YOY
            </Typography>

            {hasData && (
              <ReactApexChart
                key={`facility-ytd-${currentYear}`}
                options={{
                  chart: { type: 'bar', height: 200, toolbar: { show: false } },
                  plotOptions: {
                    bar: {
                      horizontal: true,
                      barHeight: '70%',
                      grouped: true,
                      borderRadius: 2
                    }
                  },
                  dataLabels: { enabled: false },
                  xaxis: {
                    categories: facilityData.names,
                    labels: {
                      formatter: val =>
                        val >= 1000
                          ? '$' + (val / 1000).toFixed(0) + 'K'
                          : '$0',
                      style: { fontSize: '10px' }
                    }
                  },
                  yaxis: {
                    labels: {
                      show: true,
                      style: { fontSize: '10px', fontWeight: 600 },
                      maxWidth: 130
                    }
                  },
                  colors: perYearData.map(d => d.color),
                  legend: { show: true, position: 'top', fontSize: '10px' },
                  grid: { padding: { left: 5, right: 10 } },
                  tooltip: {
                    shared: true,
                    intersect: false,
                    y: { formatter: val => '$' + val.toLocaleString() }
                  }
                }}
                series={facilityData.series}
                type='bar'
                height={200}
              />
            )}
          </SectionPaper>
        </Grid>

        {/* Cumulative Spend Trend */}
        <Grid item xs={12} md={4}>
          <SectionPaper elevation={1}>
            <Typography
              variant='subtitle2'
              fontWeight='bold'
              fontSize='0.85rem'
              mb={0.5}
            >
              Spend Trend {'YTD'} (Cumulative)
            </Typography>
            {hasData && (
              <ReactApexChart
                key={`cumulative-ytd-${currentYear}`}
                options={{
                  chart: {
                    type: 'line',
                    height: 215,
                    toolbar: { show: false }
                  },
                  stroke: { width: 2, curve: 'smooth' },
                  markers: { size: 4 },
                  xaxis: {
                    categories: cumulativeData.categories,
                    labels: { style: { fontSize: '10px' } }
                  },
                  yaxis: {
                    labels: {
                      formatter: val =>
                        val >= 1000
                          ? '$' + (val / 1000).toFixed(0) + 'K'
                          : '$0',
                      style: { fontSize: '10px' }
                    }
                  },
                  colors: perYearData.map(d => d.color),
                  legend: { show: true, position: 'top', fontSize: '10px' },
                  grid: { padding: { left: 5, right: 5 } },
                  tooltip: {
                    shared: true,
                    intersect: false,
                    y: { formatter: val => '$' + val.toLocaleString() }
                  }
                }}
                series={cumulativeData.series}
                type='line'
                height={215}
              />
            )}
          </SectionPaper>
        </Grid>
      </Grid>

      {/* ── Insights Bar ────────────────────────────────────────────────── */}
      {/* <Paper
        elevation={1}
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          backgroundColor: '#fffde7',
          borderLeft: '4px solid #f9a825'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0
          }}
        >
          <EmojiObjects sx={{ fontSize: 18, color: '#f9a825' }} />
          <Typography
            variant='caption'
            fontWeight='bold'
            fontSize='0.72rem'
            color='#795548'
          >
            Insights
          </Typography>
        </Box>
        <Divider orientation='vertical' flexItem />
        {insights.length === 0 ? (
          <Typography variant='caption' color='text.disabled'>
            No data available for insights.
          </Typography>
        ) : (
          insights.map((ins, i) => (
            <Box
              key={i}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: ins.color,
                  flexShrink: 0
                }}
              />
              <Typography
                variant='caption'
                sx={{ fontSize: '0.68rem', color: '#555' }}
              >
                {ins.text}
              </Typography>
              {i < insights.length - 1 && (
                <Divider orientation='vertical' flexItem sx={{ mx: 0.5 }} />
              )}
            </Box>
          ))
        )}
      </Paper> */}
    </Box>
  );
};

export default YOYFreightTab;
